import dayjs from 'dayjs'
import type {
	CourseAiLearnerPayment,
	CourseAiLearnerRow,
	CourseAiRefund,
	CourseAiRefundEffects,
	CourseAiRevenueFigures,
} from 'src/apis/courseAiAPI'
import { getNationalityDisplay } from 'src/pages/ActiveUsersPage/activeUserDisplay'

export const COURSE_STATUS_LABELS: Record<string, string> = {
	on_going: 'Ongoing',
	inactive: 'Inactive',
	cancel: 'Cancelled',
	complete: 'Completed',
}

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
	purchased: 'Paid',
	trial: 'Free trial',
	unpaid: 'Abandoned checkout',
}

/**
 * UD-516: the Pay column is the OnePay fee group of a payment, read from
 * OnePay's vpc_Card: an international card scheme, a domestic ATM card (bank
 * BIN), or a QR / app payment (no card). A payment saved before UD-516 has no
 * group and shows '-'.
 */
export const PAY_GROUP_LABELS: Record<string, string> = {
	international: 'Thẻ quốc tế',
	domestic: 'Thẻ ATM nội địa',
	qr_app: 'QR / App',
}

export const PAY_GROUP_FILTER_OPTIONS = [
	...Object.entries(PAY_GROUP_LABELS).map(([value, label]) => ({ value, label })),
	{ value: 'none', label: 'Not recorded' },
]

/** The group of one payment, or '' when it has none. */
export const payText = (payment?: Pick<CourseAiLearnerPayment, 'method'> | null) =>
	(payment?.method && PAY_GROUP_LABELS[payment.method]) || ''

export const PAYMENT_STATUS_FILTER_OPTIONS = [
	{ value: 'paid', label: 'Paid' },
	{ value: 'trial', label: 'Free trial' },
	{ value: 'abandoned', label: 'Abandoned' },
	{ value: 'refunded', label: 'Refunded' },
]

/**
 * UD-620: how many learners the list holds, said right above it. The cards do
 * not say it: they count paid learners only, so under Payment Status "Refunded"
 * (or "Free trial", "Abandoned") Total Learners reads 0 while the list has rows,
 * and under "Paid" it reads the same as with no filter.
 */
export const listCountText = (total: number, filtered: boolean) => {
	const learners = `${total} ${total === 1 ? 'learner' : 'learners'}`
	if (!filtered) return `${learners} in the list`
	return `${learners} ${total === 1 ? 'matches' : 'match'} the filters`
}

export const LIST_COUNT_NOTE =
	'Total Learners and the four status cards count paid learners only: no free trial, abandoned, refunded or staff rows.'

/** An unpaid row is a refunded learner when the server says so, an abandoned checkout otherwise. */
export const paymentStatusText = (row: Pick<CourseAiLearnerRow, 'payment_status' | 'refunded'>) =>
	row.refunded ? 'Refunded' : PAYMENT_STATUS_LABELS[row.payment_status] || row.payment_status

export const REFUND_STATUS_LABELS: Record<string, string> = {
	pending: 'Pending',
	refunded: 'Refunded',
	rejected: 'Rejected',
}

export const REFUND_STATUS_COLORS: Record<string, string> = {
	pending: 'yellow',
	refunded: 'red',
	rejected: 'gray',
}

export const REFUND_STATUS_FILTER_OPTIONS = Object.entries(REFUND_STATUS_LABELS).map(
	([value, label]) => ({ value, label }),
)

export const REFUND_SOURCE_LABELS: Record<string, string> = {
	app_request: 'App request',
	staff_created: 'Created by staff',
}

/** The issue types of the refund form in the app. */
export const REFUND_ISSUE_LABELS: Record<string, string> = {
	technicalIssue: 'Technical issue',
	paymentProblem: 'Payment problem',
	courseNotAsDescribed: 'Course not as described',
}

export const refundIssueText = (issue?: string | null) =>
	issue ? REFUND_ISSUE_LABELS[issue] || issue : '-'

/**
 * The refund is recorded, but a step after it (class chat, counts) did not finish.
 * Never for a refund that did not lock the course: it has no step after it.
 */
export const lockoutIncomplete = (effects?: CourseAiRefundEffects | null) =>
	!!effects &&
	effects.lockout_applied !== false &&
	(effects.class_chat === 'failed' || effects.caches_cleared === false)

/**
 * The tag next to the Refunded status: whether that refund locked the course.
 * None for a request that is not Refunded, or a server that does not say.
 */
export const refundLockTag = (
	refund: Pick<CourseAiRefund, 'status' | 'locks_course'>,
): { label: string; color: string } | null => {
	if (refund.status !== 'refunded' || typeof refund.locks_course !== 'boolean') return null
	return refund.locks_course
		? { label: 'Locked', color: 'red' }
		: { label: 'Access kept', color: 'teal' }
}

export const LOCK_COURSE_LABEL = 'Lock this course'

/** The Note box of the three refund dialogs: the server sends what staff type to the learner. */
export const REFUND_NOTE_LABEL = 'Note (sent to the learner)'

/** A refund staff created has no learner reason: the note staff typed is its reason. */
export const isStaffCreatedRefund = (refund: Pick<CourseAiRefund, 'source'>) =>
	refund.source === 'staff_created'

/** What the refunded amount box must hold: more than 0 and not more than was paid. */
export const refundAmountError = (
	value: number | '' | null | undefined,
	amountPaid: number,
): string | null => {
	if (value === '' || value === null || value === undefined || !Number.isFinite(Number(value))) {
		return 'Enter the refunded amount'
	}
	if (Number(value) <= 0) return 'The refunded amount must be greater than 0'
	if (Number(value) > Number(amountPaid)) {
		return `The refunded amount cannot be more than the amount paid (${formatVnd(amountPaid)})`
	}
	return null
}

/** The learner_type filter; the server's list (/learners/filters) wins when it has one. */
export const LEARNER_TYPE_VALUES = ['all', 'staff', 'learner']

export const LEARNER_TYPE_LABELS: Record<string, string> = {
	all: 'All',
	staff: 'Staff code',
	learner: 'Paying learners',
}

export const learnerTypeOptions = (values?: string[] | null) =>
	(values?.length ? values : LEARNER_TYPE_VALUES).map((value) => ({
		value,
		label: LEARNER_TYPE_LABELS[value] || value,
	}))

export const PAYMENT_TYPE_LABELS: Record<string, string> = {
	purchase: 'Purchase',
	repurchase: 'Repurchase',
	resume: 'Resume',
}

export const REFUND_LINE_LABEL = 'Refund'

/** One line of a learner's payment history: a payment, or a refund staff recorded. */
export interface LearnerHistoryLine {
	key: string
	kind: 'payment' | 'refund'
	date: string
	type: string
	/** A refund is a negative amount. */
	amount: number
	method: string
	transaction_ref: string
	note: string
}

/** Payments and refunds together, oldest first; a refund sits after its payment. */
export const learnerHistoryLines = (
	row: Pick<CourseAiLearnerRow, 'payments' | 'refunds'>,
): LearnerHistoryLine[] => {
	const payments: LearnerHistoryLine[] = (row.payments || []).map((payment) => ({
		key: `payment-${payment.transaction_ref}`,
		kind: 'payment',
		date: payment.paid_at,
		type: PAYMENT_TYPE_LABELS[payment.type] || payment.type,
		amount: payment.amount,
		method: payText(payment),
		transaction_ref: payment.transaction_ref,
		note: '',
	}))
	const refunds: LearnerHistoryLine[] = (row.refunds || []).map((refund) => ({
		key: `refund-${refund.id}`,
		kind: 'refund',
		date: refund.refunded_at,
		type: REFUND_LINE_LABEL,
		amount: -Math.abs(refund.amount),
		method: '',
		transaction_ref: refund.transaction_ref,
		note: refund.note || '',
	}))
	return [...payments, ...refunds]
		.map((line, index) => ({ line, index }))
		.sort(
			(a, b) =>
				dayjs(a.line.date).valueOf() - dayjs(b.line.date).valueOf() || a.index - b.index,
		)
		.map(({ line }) => line)
}

/** A row opens when it has more to show than the latest payment in the table. */
export const hasLearnerHistory = (
	row?: Pick<CourseAiLearnerRow, 'payments_count' | 'refunds'> | null,
) => (row?.payments_count || 0) > 1 || (row?.refunds?.length || 0) > 0

export const revenueRefunds = (row: CourseAiRevenueFigures) => row.refunds || 0

/** Net is gross less refunds; a server without refunds sends neither. */
export const revenueNet = (row: CourseAiRevenueFigures) =>
	row.net_revenue ?? row.revenue - revenueRefunds(row)

export const revenueHasRefunds = (rows: CourseAiRevenueFigures[]) =>
	rows.some((row) => (row.refund_count || 0) > 0 || revenueRefunds(row) !== 0)

/** The totals row of a revenue table. */
export const sumRevenue = (rows: CourseAiRevenueFigures[]): Required<CourseAiRevenueFigures> =>
	rows.reduce<Required<CourseAiRevenueFigures>>(
		(total, row) => ({
			revenue: total.revenue + row.revenue,
			transactions: total.transactions + row.transactions,
			resume_revenue: total.resume_revenue + row.resume_revenue,
			resume_transactions: total.resume_transactions + row.resume_transactions,
			refunds: total.refunds + revenueRefunds(row),
			refund_count: total.refund_count + (row.refund_count || 0),
			net_revenue: total.net_revenue + revenueNet(row),
		}),
		{
			revenue: 0,
			transactions: 0,
			resume_revenue: 0,
			resume_transactions: 0,
			refunds: 0,
			refund_count: 0,
			net_revenue: 0,
		},
	)

export const OFF_DAY_FILTER_OPTIONS = [
	{ value: '0', label: '0 days' },
	{ value: '1-3', label: '1-3 days' },
	{ value: '4-6', label: '4-6 days' },
	{ value: '7-8', label: '7-8 days' },
	{ value: '9', label: '9 days' },
]

export const formatVnd = (amount?: number | null) =>
	amount === null || amount === undefined
		? '-'
		: `${Number(amount).toLocaleString('vi-VN')} VND`

export const formatDateTime = (value?: string | null) =>
	value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'

export const nationalityText = (code?: string | null) => {
	if (!code) return '-'
	const display = getNationalityDisplay(null, code)
	return `${display.flag ? `${display.flag} ` : ''}${display.name === '-' ? code : display.name}`
}

/** The "Free 3 days" column (Admin answer 1). */
export const freeTrialText = (row: Pick<CourseAiLearnerRow, 'free_trial'>) => {
	const trial = row.free_trial
	if (!trial) return '-'
	if (trial.state === 'on_trial') {
		return trial.ends_at
			? `On trial, ends ${dayjs(trial.ends_at).format('DD/MM HH:mm')}`
			: 'On trial'
	}
	if (trial.state === 'trial_ended') return 'Trial ended, not paid'
	return 'Paid after free'
}

export const completeDaysText = (row: Pick<CourseAiLearnerRow, 'complete_days' | 'complete_days_max'>) =>
	row.complete_days === null || row.complete_days === undefined
		? '-'
		: `${row.complete_days} / ${row.complete_days_max}`

export const daysOffText = (row: Pick<CourseAiLearnerRow, 'days_off' | 'days_off_limit'>) =>
	`${row.days_off} / ${row.days_off_limit}`

const CSV_HEADER = [
	'No.',
	'Learner Name',
	'Staff code',
	'Phone',
	'Nationality',
	'Location',
	'Course',
	'Level',
	'Payment Date',
	'Amount Paid (VND)',
	'Payment Type',
	'Pay',
	'Transaction Ref',
	'Payments',
	'Complete Days',
	'Days Off',
	'Last Active',
	'Status',
	'Payment Status',
	'Free 3 days',
]

const csvCell = (value: unknown) => {
	const text = value === null || value === undefined ? '' : String(value)
	return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

const nationalityPlain = (code?: string | null) => {
	if (!code) return ''
	const name = getNationalityDisplay(null, code).name
	return name === '-' ? code : name
}

/**
 * The export: one line per payment, oldest first within a learner, and one line
 * with empty payment cells for a learner with no payment (trial or abandoned).
 * A refund staff recorded is a line of its own, type Refund, with a negative
 * amount. Starts with a UTF-8 BOM so Excel reads Vietnamese names correctly.
 */
export const buildLearnersCsv = (rows: CourseAiLearnerRow[]) => {
	const lines = [CSV_HEADER.map(csvCell).join(',')]
	for (const row of rows) {
		const history = learnerHistoryLines(row)
		const payments: (LearnerHistoryLine | null)[] = history.length ? history : [null]
		for (const payment of payments) {
			lines.push(
				[
					row.no,
					row.name,
						row.staff_code,
					row.phone,
					nationalityPlain(row.nationality),
					row.location,
					row.course_name,
					row.level,
					payment ? dayjs(payment.date).format('YYYY-MM-DD HH:mm') : '',
					payment ? payment.amount : '',
					payment ? payment.type : '',
					payment ? payment.method : '',
					payment ? payment.transaction_ref : '',
					row.payments_count,
					completeDaysText(row),
					daysOffText(row),
						row.last_active_at ? dayjs(row.last_active_at).format('YYYY-MM-DD HH:mm') : '',
					COURSE_STATUS_LABELS[row.course_status] || row.course_status,
					paymentStatusText(row),
					freeTrialText(row),
				]
					.map(csvCell)
					.join(','),
			)
		}
	}
	return `﻿${lines.join('\r\n')}\r\n`
}

export const downloadCsv = (content: string, filename: string) => {
	const blob = new Blob([content], { type: 'text/csv;charset=utf-8' })
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = filename
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}
